<template>
  <v-sheet class="py-2 px-5">
    <PersonField v-model="person" />
    <VehicleField v-model="vehicle" />
    <PeriodField v-model="period" :periods="periods" />
    <v-checkbox
      v-model="rideBorder"
      :label="$t('report.travel-expenses.ride_border')"
    />
    <v-checkbox
      v-model="homeAsAbroad"
      :label="$t('report.travel-expenses.home_as_abroad')"
    />
    <v-checkbox
      v-model="simplified"
      :label="$t('report.travel-expenses.simplified')"
    />

    <div class="text-center">
      <v-btn rounded elevation="0" color="primary" @click="openDetailDialog">{{ $t("report.travel-expenses.more_params")
        }}
      </v-btn>
    </div>

    <TravelExpensesDialog ref="detailDialog" v-on:values="closedDetailDialog" />
  </v-sheet>
</template>

<script>
import PersonField from "@/components/fields/PersonField";
import PeriodField from "@/components/fields/PeriodField";
import VehicleField from "@/components/fields/VehicleField";
import TravelExpensesDialog from "@/components/dialogs/TravelExpensesDialog";
import utils from "@/utils";
import { DateTime } from "luxon";

export default {
  name: "TravelExpensesReport",
  components: { VehicleField, PeriodField, PersonField, TravelExpensesDialog },

  data() {
    return {
      periods: [
        { name: "month-current" },
        { name: "month-previous" },
        { name: "month-one" },
        { name: "month-range" },
        { name: "day-range" },
        { name: "day-rangetime" }
      ],
      enableFetch: false,
      person: null,
      vehicle: null,
      period: null,
      rideBorder: false,
      homeAsAbroad: false,
      simplified: true,
      values: {}
    };
  },

  methods: {
    fetchReport() {
      if (this.enableFetch && this.person !== null && this.period !== null)
        this.$store.dispatch("report/fetch", this.fillValues({
          name: "travel-expenses",
          person: this.person,
          vehicle: this.vehicle,
          period: this.period,
          ride_border: this.rideBorder,
          home_as_abroad: this.homeAsAbroad,
          simplified: this.simplified,
          output: "pdf",
          download: false
        }))
          .then(response => {
            this.$emit("pdf", response.data);
          })
          .catch(() => {
            this.$emit("pdf", null);
          });
    },

    downloadReport(output) {
      if (this.person !== null && this.period !== null)
        this.$store.dispatch("report/fetch", this.fillValues({
          name: "travel-expenses",
          person: this.person,
          vehicle: this.vehicle,
          period: this.period,
          ride_border: this.rideBorder,
          home_as_abroad: this.homeAsAbroad,
          simplified: this.simplified,
          output: output,
          download: true
        }));
    },

    fillValues(params) {
      params.place_from = this.values.placeFrom || null;
      params.place_to = this.values.placeTo || null;
      params.purpose = this.values.purpose || null;
      params.deposit = utils.objectOrNull(this.values.deposit);
      params.deposit_date = this.values.depositDate || null;
      if (this.values.days !== null && this.values.days !== undefined)
        params.days = this.values.days
          .filter(x => (!x.active || x.breakfast || x.lunch || x.dinner)
                && DateTime.fromISO(x.day) <= DateTime.fromISO(this.period.to) && DateTime.fromISO(x.day) >= DateTime.fromISO(this.period.from))
          .map(x => ({
            skip: !x.active,
            day: x.day,
            breakfast: x.breakfast,
            lunch: x.lunch,
            dinner: x.dinner
          }));
      else
        params.days = null;

      return params;
    },

    openDetailDialog() {
      this.$refs.detailDialog.open(this.period, this.values);
    },

    closedDetailDialog(values) {
      this.values = values;
      this.fetchReport();
    }
  },

  watch: {
    person: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "personId", value: this.person });
    },

    vehicle: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "vehicleId", value: this.vehicle });
    },

    period: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "period", value: this.period });
    },

    rideBorder: function() {
      this.fetchReport();
    },

    homeAsAbroad: function() {
      this.fetchReport();
    },

    simplified: function() {
      this.fetchReport();
    }
  },

  mounted() {
    this.$emit("report",
      {
        name: "travel-expenses",
        download: this.downloadReport
      }
    );

    this.person = this.$router.parseQueryPersonId(this.$route) || this.$store.state.report.values.personId || null;
    this.vehicle = this.$router.parseQueryVehicleId(this.$route) || this.$store.state.report.values.vehicleId || null;
    this.period = this.$router.parseQueryPeriod(this.$route) || this.$store.state.report.values.period || null;

    setTimeout(() => {
      this.enableFetch = true;
      this.fetchReport();
    }, 1);
  }
};
</script>

<style scoped>

</style>